// src/modules/units/tyres/shared/tyre-costs.js
//
// Lifetime cost of ONE tyre: purchase + every retread + every repair, and the
// cost per km that buys (total ÷ lifetime tyreKm). Pure functions, no DOM.
//
// Sources, in order of preference:
//   - purchase: tyre.purchaseCost, else the ORIGINAL stage's cost in retreadHistory
//   - retreads: retreadHistory stages 1..n (stage 0 is the original casing and is
//     the purchase, so it is never counted twice)
//   - repairs:  tyre.repairHistory when the backend supplies it (it now does — see
//     README), else the repair entries in the action history the caller loaded.
//     The fallback only sees the page of history that was fetched, so the result
//     says which source it used (`repairSource`) and the view can caveat it.
//
// Unknowns stay null — a tyre with no known costs has a null total, not R 0.

import { resolveActionType, TYRE_ACTION } from './tyre-action-schema.js';
import { tyreLifeStages } from './tyre-life-stages.js';

const isBlank = (v) => v === null || v === undefined || v === '';
const money = (v) => {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const instant = (v) => { const t = new Date(v).getTime(); return Number.isNaN(t) ? null : t; };

/** Repairs as { date, cost } (cost may be null), newest first. */
export function tyreRepairs(tyre = {}, actions = []) {
  let list;
  if (Array.isArray(tyre.repairHistory)) {
    list = tyre.repairHistory.map((r) => ({
      date: r?.date ?? r?.timestamp ?? r?.createdAt ?? null,
      cost: money(r?.cost ?? r?.serviceDeliveryCost)
    }));
  } else {
    list = (Array.isArray(actions) ? actions : [])
      .filter((a) => resolveActionType(a) === TYRE_ACTION.REPAIR)
      .map((a) => ({
        date: a.timestamp || a.createdAt || null,
        cost: money(a.details?.serviceDeliveryCost ?? a.serviceDeliveryCost)
      }));
  }
  return list.sort((a, b) => (instant(b.date) ?? 0) - (instant(a.date) ?? 0));
}

/** The newest repair that recorded a cost, or null. */
export function latestRepairCost(tyre = {}, actions = []) {
  return tyreRepairs(tyre, actions).find((r) => r.cost !== null) || null;
}

export function tyreLifetimeCost(tyre = {}, actions = []) {
  const stages = tyreLifeStages(tyre);
  const original = stages.find((s) => Number(s.stageNo) === 0);
  const purchase = money(tyre.purchaseCost) ?? money(original?.cost);

  const retreadStages = stages.filter((s) => Number(s.stageNo) > 0);
  const retreadCosts = retreadStages.map((s) => money(s.cost)).filter((c) => c !== null);
  const retreads = retreadCosts.length ? retreadCosts.reduce((s, c) => s + c, 0) : null;

  const repairList = tyreRepairs(tyre, actions);
  const repairCosts = repairList.map((r) => r.cost).filter((c) => c !== null);
  const repairs = repairCosts.length ? repairCosts.reduce((s, c) => s + c, 0) : null;

  // The backend's own totals win when present (it sees every repair, not just a
  // page of history); the local sum is the fallback for older records. A tyre
  // never costs R 0, so a backend 0 means "nothing to total" and is treated as
  // unknown rather than printed as R 0.00.
  const positive = (v) => { const n = money(v); return n !== null && n > 0 ? n : null; };
  const parts = [purchase, retreads, repairs].filter((c) => c !== null);
  const total = positive(tyre.lifetimeCost) ?? (parts.length ? parts.reduce((s, c) => s + c, 0) : null);

  const km = Number(tyre.tyreKm);
  const costPerKm = positive(tyre.costPerKm)
    ?? (total !== null && total > 0 && Number.isFinite(km) && km > 0 ? total / km : null);

  return {
    purchase,
    retreads,
    retreadCount: retreadStages.length,
    repairs,
    repairCount: repairList.length,
    repairSource: Array.isArray(tyre.repairHistory) ? 'repairHistory' : 'actions',
    total,
    km: Number.isFinite(km) && km > 0 ? km : null,
    costPerKm
  };
}
